import { Link } from "react-router-dom";
import { Phone, ArrowRight, CheckCircle, Medal, Shield, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import TrustBadges from "@/components/TrustBadges";
import ServiceCard from "@/components/ServiceCard";
import ContactForm from "@/components/ContactForm";
import FAQ from "@/components/FAQ";
import heroImage from "@/assets/hero-plumber.webp";
import {
  BUSINESS_NAME,
  BUSINESS_TAGLINE,
  BUSINESS_DESCRIPTION,
  PHONE,
  PHONE_LINK,
  PLUMBING_SERVICES,
  PURIFICATION_SERVICES,
  WEST_VALLEY_CITIES,
  AREA_SERVED_SCHEMA,
  ORG_SCHEMA,
  BBB_URL,
} from "@/lib/constants";

const homeFaqs = [
  {
    question: "What areas does Tru American Plumbing Purifications serve?",
    answer:
      "We proudly serve Tolleson, Arizona, and the entire West Valley including Avondale, Goodyear, Glendale, Peoria, Surprise, Buckeye, and West Phoenix. We specialize in residential and light commercial plumbing services.",
  },
  {
    question: "Do you offer emergency plumbing services?",
    answer:
      "Yes, we provide fast response emergency plumbing services. When you have a plumbing emergency like a burst pipe, major leak, or sewer backup, call us immediately and we'll dispatch a licensed plumber to your location as quickly as possible.",
  },
  {
    question: "Are you licensed and insured?",
    answer:
      "Absolutely. Tru American Plumbing Purifications LLC is fully licensed, bonded, and insured in the state of Arizona. This protects you and your property while ensuring you receive professional, code-compliant plumbing work.",
  },
  {
    question: "What is hard water and why is it a problem in Arizona?",
    answer:
      "Arizona has some of the hardest water in the country due to high mineral content, primarily calcium and magnesium. Hard water causes scale buildup in pipes and appliances, reduces water heater efficiency, leaves spots on fixtures, and can dry out skin and hair. Our water softener and purification systems solve these problems.",
  },
  {
    question: "How do I know if I need a water softener?",
    answer:
      "Signs you need a water softener include: white scale buildup on faucets and showerheads, spots on dishes after washing, dry skin and hair, clothes fading quickly, and higher water heating costs. We offer free water quality assessments to help you decide.",
  },
  {
    question: "Do you offer discounts for veterans and military?",
    answer:
      "Yes! As a veteran-owned business, we proudly offer special discounts to veterans, active-duty military, and first responders. We believe in giving back to those who serve our country and community. Call us at (480) 322-2567 to claim your discount when scheduling service.",
  },
];

const Index = () => {
  const baseUrl = "https://truamericanplumbing.com";

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: homeFaqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Plumbing and Water Purification",
    provider: { "@type": "Plumber", "@id": `${baseUrl}/#organization` },
    areaServed: AREA_SERVED_SCHEMA,
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Plumbing & Water Purification Services",
      itemListElement: [
        ...PLUMBING_SERVICES.map((s) => ({
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: s.title, url: `${baseUrl}/services/${s.slug}` },
        })),
        ...PURIFICATION_SERVICES.map((s) => ({
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: s.title, url: `${baseUrl}/water-purification/${s.slug}` },
        })),
      ],
    },
  };

  return (
    <>
      <SEO
        title="Plumber in Tolleson, AZ — Veteran-Owned & Family-Operated"
        description="Looking for a trusted plumber in Tolleson, AZ? Tru American Plumbing Purifications is the veteran-owned, locally-based plumber serving Tolleson and the West Valley. Licensed, bonded & insured. Call (480) 322-2567."
        canonicalUrl="/"
        type="local_business"
        schema={[ORG_SCHEMA, faqSchema, serviceSchema]}
      />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Professional plumber servicing water heater in Tolleson, AZ"
            className="w-full h-full object-cover"
            width={1920}
            height={1080}
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-background/40" />
        </div>

        <div className="container relative z-10 py-20 md:py-32">
          <div className="max-w-2xl animate-slide-up">
            <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/30 rounded-full px-4 py-2 mb-6">
              <Medal className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">{BUSINESS_TAGLINE}</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Plumber in{" "}
              <span className="text-gradient-copper">Tolleson, AZ</span>
            </h1>
            <p className="text-xl md:text-2xl text-foreground/80 mb-8 leading-relaxed">
              Veteran-owned, family-operated and headquartered right here in Tolleson (85353). Expert plumbing and water purification for homes across Tolleson, Avondale, Goodyear, Glendale and the entire West Valley — fast response, honest pricing, and American quality you can count on.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <a href={PHONE_LINK}>
                <Button variant="hero" size="xl" className="w-full sm:w-auto gap-2">
                  <Phone className="h-5 w-5" />
                  Call Now: {PHONE}
                </Button>
              </a>
              <Link to="/contact/">
                <Button variant="heroOutline" size="xl" className="w-full sm:w-auto gap-2">
                  Request Service
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-4 text-sm text-foreground/70">
              <span className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                Licensed & Bonded
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                Fully Insured
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                Serving the West Valley & West Phoenix
              </span>
            </div>
            <details className="mt-6 md:open group" open>
              <summary className="md:hidden cursor-pointer text-sm font-semibold text-primary list-none flex items-center gap-1">
                Read more about our service area
                <ArrowRight className="h-3 w-3 transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-3 md:mt-0 text-sm text-foreground/60 max-w-xl leading-relaxed">
              Tru American Plumbing Purifications LLC is a veteran-owned plumbing company based in Tolleson, Arizona (ZIP 85353), near the 91st Avenue and I-10 corridor. We provide residential and light commercial plumbing repair, installation, water heater service, drain cleaning, slab leak detection, and water treatment to homeowners throughout Tolleson and the West Valley — including Avondale, Goodyear, Glendale, Peoria, Surprise, Buckeye, Sun City, El Mirage, Litchfield Park and West Phoenix. Because we're headquartered in Tolleson, not driving in from across the metro, we offer some of the fastest response times in the area.
            </p>
            </details>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <TrustBadges />

      {/* Services Section */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Professional{" "}
              <span className="text-gradient-copper">Plumbing Services</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Arizona homeowners face unique plumbing challenges — from copper pipe corrosion caused by hard water to slab leaks common in the region's concrete foundations. Extreme summer heat also shortens water heater lifespans, often requiring earlier replacement than in other climates. Our licensed plumbers diagnose and resolve these issues daily across the West Valley.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PLUMBING_SERVICES.map((service) => (
              <ServiceCard key={service.slug} {...service} />
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/services/">
              <Button variant="outline" size="lg" className="gap-2">
                View All Plumbing Services
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Water Purification Section */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Protecting Your Home from{" "}
              <span className="text-gradient-copper">Arizona's Harsh Water</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              The Phoenix Metro area consistently ranks among the hardest water regions in the United States, with mineral concentrations often exceeding 15 grains per gallon. This leads to scale buildup inside pipes, reduced appliance efficiency, and poor-tasting tap water. Our water softener and reverse osmosis systems are sized specifically for Arizona water conditions and installed by licensed plumbers.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {PURIFICATION_SERVICES.map((service) => (
              <ServiceCard
                key={service.slug}
                {...service}
                basePath="/water-purification"
              />
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/water-purification/">
              <Button variant="outline" size="lg" className="gap-2">
                Explore Water Solutions
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Why Choose{" "}
                <span className="text-gradient-copper">Tru American?</span>
              </h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Founded in Tolleson, Tru American Plumbing Purifications serves single-family homes, townhomes, and small commercial properties across the West Valley. As a veteran-owned, family-operated business, we bring a straightforward approach to plumbing — clear diagnostics, upfront pricing, and work that meets Arizona plumbing codes every time.
              </p>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 gradient-copper rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Local West Valley Experts</h3>
                    <p className="text-muted-foreground">
                      We're your neighbors. Based in Tolleson, we understand the
                      unique plumbing challenges Arizona homeowners face.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 gradient-copper rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Clear Communication</h3>
                    <p className="text-muted-foreground">
                      No surprises. We explain the problem, discuss solutions, and
                      provide upfront pricing before any work begins.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 gradient-copper rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Clean, Professional Work</h3>
                    <p className="text-muted-foreground">
                      We treat your home with respect. Our technicians wear shoe
                      covers and clean up after every job.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 gradient-copper rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Licensed, Bonded & Insured</h3>
                    <p className="text-muted-foreground">
                      Full protection for you and your property. All work meets
                      Arizona plumbing codes and regulations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-6 text-center">
                Request Service Today
              </h3>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Veteran Discount Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-primary/10 via-background to-primary/5 border-y border-primary/20">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 gradient-copper rounded-2xl mb-6">
              <Shield className="h-8 w-8 text-primary-foreground" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Veterans & <span className="text-gradient-copper">First Responders</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-4 max-w-2xl mx-auto">
              As a veteran-owned business, we're proud to give back to those who serve.
              We offer special discounts to veterans, active-duty military, and first responders
              on all plumbing and water purification services.
            </p>
            <p className="text-foreground/70 mb-8">
              Thank you for your service — let us serve you.
            </p>
            <a href={PHONE_LINK}>
              <Button variant="hero" size="xl" className="gap-2">
                <Phone className="h-5 w-5" />
                Call to Claim Your Discount: {PHONE}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Serving the{" "}
              <span className="text-gradient-copper">West Valley & West Phoenix</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Proudly providing plumbing and water purification services throughout
              the West Valley and West Phoenix.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {WEST_VALLEY_CITIES.slice(0, 12).map((city) => (
              <Link
                key={city}
                to={`/service-areas/${city.toLowerCase().replace(/\s+/g, "-")}-az/`}
                className="px-4 py-2 bg-card border border-border rounded-full text-sm hover:border-primary/50 hover:text-primary transition-colors"
              >
                {city}, AZ
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/service-areas/">
              <Button variant="outline" size="lg" className="gap-2">
                View All Service Areas
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Accreditations & Profiles */}
      <section className="py-10 border-t border-border">
        <div className="container">
          <div className="flex flex-wrap items-center justify-center gap-8">
            <a
              href={BBB_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 px-6 py-3 border border-border rounded-xl hover:border-primary/40 transition-colors"
              aria-label="BBB Accredited Business profile"
            >
              <div className="flex items-center justify-center w-10 h-10 gradient-copper rounded-lg">
                <Shield className="h-5 w-5 text-primary-foreground" />
              </div>
              <div className="text-left">
                <span className="block text-sm font-bold text-foreground group-hover:text-primary transition-colors">BBB Accredited</span>
                <span className="block text-xs text-muted-foreground">Better Business Bureau</span>
              </div>
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61587622751294"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 px-6 py-3 border border-border rounded-xl hover:border-primary/40 transition-colors"
              aria-label="Facebook page"
            >
              <div className="flex items-center justify-center w-10 h-10 gradient-copper rounded-lg">
                <svg className="h-5 w-5 text-primary-foreground" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </div>
              <div className="text-left">
                <span className="block text-sm font-bold text-foreground group-hover:text-primary transition-colors">Facebook</span>
                <span className="block text-xs text-muted-foreground">Follow Us</span>
              </div>
            </a>
            <a
              href="https://www.instagram.com/truamericanplumbing26/"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 px-6 py-3 border border-border rounded-xl hover:border-primary/40 transition-colors"
              aria-label="Instagram profile"
            >
              <div className="flex items-center justify-center w-10 h-10 gradient-copper rounded-lg">
                <svg className="h-5 w-5 text-primary-foreground" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </div>
              <div className="text-left">
                <span className="block text-sm font-bold text-foreground group-hover:text-primary transition-colors">Instagram</span>
                <span className="block text-xs text-muted-foreground">Follow Us</span>
              </div>
            </a>
            <a
              href="https://www.google.com/maps/place/Tru+American+Plumbing+Purifications/@33.3542164,-112.4815895,135969m/data=!3m2!1e3!4b1!4m6!3m5!1s0x2bed6e721a1d8217:0x858deda89e0d0478!8m2!3d33.3542164!4d-112.4815896!16s%2Fg%2F11yybx_jx7?entry=ttu&g_ep=EgoyMDI2MDMwOC4wIKXMDSoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 px-6 py-3 border border-border rounded-xl hover:border-primary/40 transition-colors"
              aria-label="Google Business Profile"
            >
              <div className="flex items-center justify-center w-10 h-10 gradient-copper rounded-lg">
                <MapPin className="h-5 w-5 text-primary-foreground" />
              </div>
              <div className="text-left">
                <span className="block text-sm font-bold text-foreground group-hover:text-primary transition-colors">Google</span>
                <span className="block text-xs text-muted-foreground">Business Profile</span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ faqs={homeFaqs} />

    </>
  );
};

export default Index;
